import { AlertTriangle } from 'lucide-react';
import { EmptyState } from './empty-state';

export interface ErrorStateProps {
  /** Refetch handler — wired straight to the query's `refetch`. */
  onRetry: () => void;
  title?: string;
  description?: string;
  /** Compact mode (smaller padding/icon) — matches EmptyState's `compact`. */
  compact?: boolean;
  className?: string;
}

/**
 * ErrorState — the canonical `isError` render, an `EmptyState` variant with a
 * wired Retry action. Rule 59: a query failure resolves to THIS, never a
 * permanent skeleton or a silent blank. Never hand-roll an error card with
 * its own retry button — compose this on top of `EmptyState` (rule: one
 * canonical per concept).
 *
 * ```tsx
 * const { data, isError, refetch } = useQuery(...);
 * if (isError) return <ErrorState onRetry={refetch} />;
 * ```
 */
export function ErrorState({
  onRetry,
  title = 'Something went wrong',
  description = 'This didn’t load. Try again.',
  compact,
  className,
}: ErrorStateProps) {
  return (
    <EmptyState
      icon={AlertTriangle}
      title={title}
      description={description}
      action={{ label: 'Retry', onClick: onRetry, variant: 'outline' }}
      compact={compact}
      className={className}
    />
  );
}
